// @ts-nocheck
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { LinkPreviewData } from '@/components/social-wall/LinkPreviewCard';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;

export function useLinkPreview() {
  const [linkPreview, setLinkPreview] = useState<LinkPreviewData | null>(null);
  const [detectedUrl, setDetectedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRemoved, setIsRemoved] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchedUrlRef = useRef<string | null>(null);

  const fetchPreview = useCallback(async (url: string) => {
    if (lastFetchedUrlRef.current === url) return;
    
    setIsLoading(true);
    lastFetchedUrlRef.current = url;

    try {
      const { data, error } = await invokeEdgeFunction('fetch-link-preview', {
        body: { url },
      });

      if (error) {
        console.error('Error fetching link preview:', error);
        setLinkPreview(null);
        return;
      }

      if (data && !data.error) {
        setLinkPreview(data as LinkPreviewData);
        setDetectedUrl(url);
      } else {
        setLinkPreview(null);
      }
    } catch (err) {
      console.error('Error fetching link preview:', err);
      setLinkPreview(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const detectAndFetchUrl = useCallback((text: string) => {
    // If user manually removed the preview, don't re-fetch for same URL
    if (isRemoved && detectedUrl) {
      const matches = text.match(URL_REGEX);
      const firstUrl = matches?.[0];
      if (firstUrl === detectedUrl) return;
      // Reset removed state if URL changed
      setIsRemoved(false);
    }

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce URL detection
    debounceTimerRef.current = setTimeout(() => {
      const matches = text.match(URL_REGEX);
      const firstUrl = matches?.[0];

      if (firstUrl && firstUrl !== lastFetchedUrlRef.current) {
        fetchPreview(firstUrl);
      } else if (!firstUrl) {
        // No URL found, clear preview
        setLinkPreview(null);
        setDetectedUrl(null);
        lastFetchedUrlRef.current = null;
      }
    }, 500);
  }, [fetchPreview, isRemoved, detectedUrl]);

  const removePreview = useCallback(() => {
    setLinkPreview(null);
    setIsRemoved(true);
  }, []);

  const reset = useCallback(() => {
    setLinkPreview(null);
    setDetectedUrl(null);
    setIsRemoved(false);
    setIsLoading(false);
    lastFetchedUrlRef.current = null;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    linkPreview,
    detectedUrl,
    isLoading,
    detectAndFetchUrl,
    removePreview,
    reset,
  };
}
